import { Button, Modal, type ModalProps, useModal } from '@faceup/ui-base'
import { useId } from '@mantine/hooks'
import type { MouseEvent, ReactNode } from 'react'
import type { FieldValues } from 'react-hook-form'
import { useFormLocalization } from '../FormLocalizationProvider'
import { FormItemsWrapper } from './FormItemsWrapper'
import { type ReactHookFormProps, useFormLogic } from './useFormLogic'

export type ModalFormProps<FV extends FieldValues = FieldValues> = ModalProps &
  ReactHookFormProps<FV> & {
    onCancel?: () => void
    isCancelButtonHidden?: boolean
    additionalContent?: ReactNode
  }

export const ModalForm = <FV extends FieldValues = FieldValues>({
  onCancel,
  isCancelButtonHidden = false,
  additionalContent,
  ...props
}: ModalFormProps<FV>) => {
  const { modalForm, leaveUnsavedChanges, leaveUnsavedChangesTitle } = useFormLocalization()
  const modal = useModal()
  const formId = useId()
  const { children, formProps, modalProps } = splitProps(props)

  const {
    onSubmit,
    submitButtonText,
    additionalButtons,
    isLoading,
    isSubmitButtonDisabled,
    hasUnsavedChanges,
  } = useFormLogic(formProps)

  const handleClose = (e: MouseEvent<HTMLButtonElement>) => {
    const target = e.target as HTMLElement
    const className = typeof target.className === 'string' ? target.className : ''
    const isClickedIntoBackdrop = className.includes('ant-modal-wrap')
    const shouldShowAlert = hasUnsavedChanges && isClickedIntoBackdrop

    const close = () => {
      props.form.reset()
      modalProps.onClose?.(e)
    }

    if (!shouldShowAlert) {
      close()
      return
    }

    modal.confirm.warning({
      title: leaveUnsavedChangesTitle,
      content: leaveUnsavedChanges,
      onConfirm: close,
    })
  }

  return (
    <Modal
      {...modalProps}
      onClose={modalProps.onClose ? handleClose : undefined}
      footer={
        <div className='mt-8 flex items-center justify-between gap-2'>
          <div>{additionalContent}</div>
          <div data-onboarding='modal-form-buttons' className='flex justify-end gap-2'>
            {additionalButtons}
            {!isCancelButtonHidden && (
              <Button type='default' onClick={onCancel ?? handleClose}>
                {modalForm.cancelButton}
              </Button>
            )}
            <Button
              type='primary'
              loading={isLoading}
              disabled={isSubmitButtonDisabled}
              form={formId}
              data-test='modal-form-submit-button'
              htmlType='submit'
            >
              {submitButtonText}
            </Button>
          </div>
        </div>
      }
    >
      <form id={formId} onSubmit={onSubmit} className='w-full'>
        <FormItemsWrapper>{children}</FormItemsWrapper>
      </form>
    </Modal>
  )
}

type FnSplitProps = <FV extends FieldValues = FieldValues>(
  props: ModalFormProps<FV>
) => {
  children?: ReactNode
  modalProps: Omit<ModalProps, 'children'>
  formProps: ReactHookFormProps<FV>
}
// Props must be separated because onSubmit in Modal made problems
const splitProps: FnSplitProps = <FV extends FieldValues = FieldValues>(
  props: ModalFormProps<FV>
) => {
  const {
    children,
    onSubmit,
    form,
    submitButtonText,
    customSubmitButtonText,
    customSubmitSuccessText,
    additionalButtons,
    isSubmitButtonDisabled,
    isSuccessMessageDisplayed,
    ...modalProps
  } = props
  const formProps: ReactHookFormProps<FV> = {
    onSubmit,
    form,
    submitButtonText,
    customSubmitButtonText,
    customSubmitSuccessText,
    additionalButtons,
    isSubmitButtonDisabled,
    isSuccessMessageDisplayed,
  } as ReactHookFormProps<FV>

  return {
    formProps,
    modalProps,
    children,
  }
}
